import React from 'react';
import Input from 'formik-antd/lib/input';
import { Switch } from './Switch';
import Form from 'formik-antd/lib/form';
import {
  Flex,
  Grid,
  Segment,
  FormGroup,
  RemoteAccessWEBProfile,
  webValidationSchema,
} from '@mss/mss-component-library';
import { Formik, ErrorMessage } from 'formik';
import emptyProfiles from './emptyProfiles';
import { useTranslation } from 'react-i18next';
import SubmitButton from 'jsx/common/SubmitButton';
import Button from 'jsx/common/Button';
import { Trash } from 'react-feather';
import ToggleEnabled from './ToggleEnabled';
import { ProfileCollapse } from './ProfileCollapse';

type WEBCollapseProps = {
  onSubmit: (values: any) => any;
  profile?: RemoteAccessWEBProfile & { id: string };
  asset: string;
  deleteProfile?: (id: string) => void;
  readonly?: boolean;
};

const WEBCollapse = ({
  onSubmit,
  profile,
  asset,
  deleteProfile,
  readonly,
}: WEBCollapseProps) => {
  const [t] = useTranslation(['remoteAccess', 'common', 'asset']);
  const toggleEnabled = () => {
    if (!readonly) onSubmit({ ...profile, enabled: !profile?.enabled });
  };
  return (
    <Formik
      validationSchema={webValidationSchema(t)}
      onSubmit={onSubmit}
      initialValues={
        profile
          ? { ...emptyProfiles.web, ...profile, asset }
          : { ...emptyProfiles.web, asset }
      }
    >
      {({ values, isSubmitting, errors, setFieldValue, setFieldTouched }) => {
        const hasErrors = Object.keys(errors).length > 0;

        return (
          <ProfileCollapse
            defaultActiveKey={profile ? undefined : 'web'}
            bordered={false}
            expandIconPosition="end"
          >
            <ProfileCollapse.Panel
              key="web"
              header={
                <span>
                  {t('WEB')} - {values.name}{' '}
                  {!profile && (
                    <strong style={{ color: 'red' }}>{t('Unsaved')}</strong>
                  )}
                </span>
              }
              extra={
                <ToggleEnabled
                  setFieldValue={setFieldValue}
                  t={t}
                  checked={values.enabled}
                  onSubmit={profile ? toggleEnabled : undefined}
                  disabled={readonly}
                />
              }
            >
              <Form>
                <Grid.Container>
                  <Segment
                    heading={
                      <Flex v="center" h={deleteProfile ? 'between' : 'start'}>
                        <span>{t('Connection Settings')}</span>
                        {deleteProfile && profile && (
                          <Button
                            size="small"
                            onClick={() => deleteProfile(profile.id)}
                            data-ta="WEBCollapseDeleteButton"
                          >
                            <Flex v="center">
                              <Trash size={14} className="mr-1" />
                              {t('Delete')}
                            </Flex>
                          </Button>
                        )}
                      </Flex>
                    }
                  >
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="name" />}
                          label={t('Name')}
                          required
                          input={
                            <Input
                              disabled={readonly}
                              name="name"
                              value={values.name}
                              onInput={() =>
                                setFieldTouched(`name`, true, true)
                              }
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={
                            <ErrorMessage
                              name="settings.url"
                              render={() =>
                                t('Please enter a valid URL')
                              }
                            />
                          }
                          label={t('URL')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.url"
                              value={values.settings.url || ''}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.limit_network" />}
                          label={t('Limit network connectivity')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.limit_network"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Host Settings')}>
                    <Grid.Row>
                      <Grid.Col width={6}>
                        <FormGroup
                          error={<ErrorMessage name="settings.hosts" />}
                          label={t('Hosts file')}
                          input={
                            <Input.TextArea
                              disabled={readonly}
                              name="settings.hosts"
                              value={values.settings.hosts}
                              rows={10}
                              style={{ width: '100%' }}
                              placeholder={
                                '127.0.0.1  some-host\n192.168.1.1  another-host another-host-alias'
                              }
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Session Recording Settings')}>
                    <Grid.Row>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Record session')}
                          input={
                            <Switch
                              name="record_session"
                              size="small"
                              data-ta="RecordSessionSwitch"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Exclude output')}
                          input={
                            <Switch
                              name="settings.recording-exclude-output"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Exclude mouse')}
                          input={
                            <Switch
                              name="settings.recording-exclude-mouse"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Include keys')}
                          input={
                            <Switch
                              name="settings.recording-include-keys"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Clipboard')}>
                    <Grid.Row>
                      <Grid.Col width={12}>
                        <table>
                          <tbody>
                            <tr>
                              <td>
                                <Switch
                                  disabled={readonly}
                                  name="settings.disable-copy"
                                  size="small"
                                />
                              </td>
                              <td>
                                {t('Disable copying from remote desktop')}
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <Switch
                                  disabled={readonly}
                                  name="settings.disable-paste"
                                  size="small"
                                />
                              </td>
                              <td>{t('Disable pasting from client')}</td>
                            </tr>
                          </tbody>
                        </table>
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>

                  <Grid.Row>
                    <Grid.Col width={12} className="mt-4">
                      <Flex h="end" v="center">
                        <SubmitButton
                          loading={isSubmitting}
                          disabled={
                            isSubmitting ||
                            hasErrors ||
                            !values.name ||
                            readonly
                          }
                        >
                          {t('Save profile')}
                        </SubmitButton>
                      </Flex>
                    </Grid.Col>
                  </Grid.Row>
                </Grid.Container>
              </Form>
            </ProfileCollapse.Panel>
          </ProfileCollapse>
        );
      }}
    </Formik>
  );
};

export default WEBCollapse;
